/** Recovery grants are one-use; React effect cleanup must not consume them twice. */
export type RecoveryLinkParams = {
  code?: string
  tokenHash?: string
  accessToken?: string
  refreshToken?: string
  errorCode?: string
}

export type RecoverySession = { user: { id: string }; access_token?: string }

export type RecoverySessionProof = { userId: string; sessionId: string | null; expiresAt: number }
type ProofStorage = {
  get(): unknown
  set(proof: RecoverySessionProof): void
  clear(): void
}

function sessionId(session: RecoverySession): string | null {
  try {
    const encoded = session.access_token?.split('.')[1]
    if (!encoded) return null
    const payload = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))) as {
      session_id?: unknown
    }
    return typeof payload.session_id === 'string' ? payload.session_id : null
  } catch {
    return null
  }
}

type SessionResult<T extends RecoverySession> = {
  data: { session: T | null }
  error: unknown
}

export type RecoveryAuthClient<T extends RecoverySession> = {
  getSession(): Promise<SessionResult<T>>
  verifyOtp(params: { token_hash: string; type: 'recovery' }): Promise<SessionResult<T>>
  exchangeCodeForSession(code: string): Promise<SessionResult<T>>
  setSession(params: { access_token: string; refresh_token: string }): Promise<SessionResult<T>>
}

function readSearchParams(params: URLSearchParams): RecoveryLinkParams {
  return {
    code: params.get('code') || undefined,
    tokenHash: params.get('token_hash') || params.get('tokenHash') || undefined,
    accessToken: params.get('access_token') || params.get('accessToken') || undefined,
    refreshToken: params.get('refresh_token') || params.get('refreshToken') || undefined,
    errorCode: params.get('error_code') || params.get('error') || undefined,
  }
}

export function hasRecoveryMaterial(params: RecoveryLinkParams): boolean {
  return Boolean(params.tokenHash || params.code || (params.accessToken && params.refreshToken))
}

/** Keep an entire grant together; never mix a warm link with an old initial URL. */
export function mergeRecoveryParams(
  preferred: RecoveryLinkParams,
  fallback: RecoveryLinkParams,
): RecoveryLinkParams {
  return hasRecoveryMaterial(preferred) || preferred.errorCode ? preferred : fallback
}

export function parseRecoveryParamsFromUrl(rawUrl: string | null | undefined): RecoveryLinkParams {
  if (!rawUrl) return {}
  try {
    const parsed = new URL(rawUrl)
    return mergeRecoveryParams(
      readSearchParams(parsed.searchParams),
      readSearchParams(new URLSearchParams(parsed.hash.replace(/^#/, ''))),
    )
  } catch {
    const [query = '', hash = ''] = rawUrl.split('#')
    const queryString = query.includes('?') ? query.slice(query.indexOf('?') + 1) : query
    return mergeRecoveryParams(
      readSearchParams(new URLSearchParams(queryString)),
      readSearchParams(new URLSearchParams(hash)),
    )
  }
}

export function isPasswordRecoveryPath(pathname: string): boolean {
  return /^\/(?:\(auth\)\/)?reset-password(?:\/|$)/.test(pathname)
}

/** Password validation errors never invalidate the authenticated recovery session. */
export function isInvalidPasswordRecoverySessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const item = error as { code?: unknown; name?: unknown; message?: unknown }
  const code = typeof item.code === 'string' ? item.code : ''
  const message = typeof item.message === 'string' ? item.message : ''
  return (
    /^(AUTH_SESSION_MISSING|session_not_found|refresh_token_not_found|refresh_token_already_used|bad_jwt)$/i.test(
      code,
    ) ||
    item.name === 'AuthSessionMissingError' ||
    /PASSWORD_RECOVERY_(LINK_MISSING_TOKEN|SESSION_MISSING|SESSION_CHANGED)|auth session missing/i.test(
      message,
    )
  )
}

/** Only an invalid grant/session needs a new email. Network and rate limits can retry. */
export function isInvalidPasswordRecoveryError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const item = error as { code?: unknown; name?: unknown; message?: unknown; status?: unknown }
  const code = typeof item.code === 'string' ? item.code : ''
  if (/^(same_password|weak_password)$/i.test(code)) return false
  return (
    /^(otp_expired|access_denied|bad_code_verifier|flow_state_expired|flow_state_not_found|session_not_found|refresh_token_not_found|refresh_token_already_used|invalid_grant|bad_jwt)$/i.test(
      code,
    ) ||
    isInvalidPasswordRecoverySessionError(error) ||
    item.status === 400 ||
    item.status === 401 ||
    item.status === 403 ||
    item.status === 422
  )
}

/**
 * One coordinator per client/runtime, outside React. A remount joins the same
 * promise and gets its own completion handler; cancellation only stops UI work.
 * Failed grants never fall back to an unrelated session that happened to exist.
 */
export function createPasswordRecoverySessionCoordinator<T extends RecoverySession>(
  auth: RecoveryAuthClient<T>,
  options: { storage?: ProofStorage; now?: () => number } = {},
) {
  const attempts = new Map<string, Promise<T>>()
  const now = options.now ?? Date.now
  let verifiedProof: RecoverySessionProof | null = null
  let cleanupProof: RecoverySessionProof | null = null
  let installedProof: { proof: RecoverySessionProof; generation: number } | null = null
  let completedKey: string | null = null
  let generation = 0
  let queue: Promise<void> = Promise.resolve()

  try {
    const raw = options.storage?.get()
    if (raw && typeof raw === 'object') {
      const proof = raw as Partial<RecoverySessionProof>
      if (
        typeof proof.userId === 'string' &&
        proof.userId &&
        (proof.sessionId === null || typeof proof.sessionId === 'string') &&
        typeof proof.expiresAt === 'number' &&
        proof.expiresAt > now() &&
        proof.expiresAt <= now() + 60 * 60 * 1000
      ) {
        verifiedProof = proof as RecoverySessionProof
      }
    }
  } catch {
    // Browser storage can be disabled; a fresh recovery grant still works.
  }

  function matchesProof(session: T | null, proof: RecoverySessionProof | null): boolean {
    return Boolean(
      session &&
      proof &&
      session.user.id === proof.userId &&
      (!proof.sessionId || sessionId(session) === proof.sessionId),
    )
  }

  async function requireVerifiedSession(
    proof: RecoverySessionProof | null,
    expectedGeneration = generation,
  ): Promise<T> {
    const result = await auth.getSession()
    if (result.error) throw result.error
    if (
      expectedGeneration !== generation ||
      !proof ||
      proof.expiresAt <= now() ||
      !result.data.session
    ) {
      throw new Error('PASSWORD_RECOVERY_SESSION_MISSING')
    }
    if (!matchesProof(result.data.session, proof)) {
      throw new Error('PASSWORD_RECOVERY_SESSION_CHANGED')
    }
    return result.data.session
  }

  function establish(params: RecoveryLinkParams): Promise<T> {
    if (params.errorCode) {
      return Promise.reject(
        Object.assign(new Error('PASSWORD_RECOVERY_LINK_INVALID'), {
          code: params.errorCode,
          status: 403,
        }),
      )
    }
    if (!hasRecoveryMaterial(params)) return requireVerifiedSession(verifiedProof)

    // A token_hash email works across devices; it must not depend on a stale
    // PKCE verifier when a legacy URL also happens to carry a code.
    const key = params.tokenHash
      ? `hash:${params.tokenHash}`
      : params.accessToken && params.refreshToken
        ? `session:${params.accessToken}:${params.refreshToken}`
        : `code:${params.code}`
    let attempt = attempts.get(key)
    if (!attempt) {
      const attemptGeneration = generation
      const run = async (): Promise<T> => {
        let result: SessionResult<T>
        if (params.tokenHash) {
          result = await auth.verifyOtp({ token_hash: params.tokenHash, type: 'recovery' })
        } else if (params.accessToken && params.refreshToken) {
          result = await auth.setSession({
            access_token: params.accessToken,
            refresh_token: params.refreshToken,
          })
        } else {
          result = await auth.exchangeCodeForSession(params.code!)
        }
        if (result.error) throw result.error
        if (!result.data.session) throw new Error('PASSWORD_RECOVERY_SESSION_MISSING')
        const proof = {
          userId: result.data.session.user.id,
          sessionId: sessionId(result.data.session),
          expiresAt: now() + 60 * 60 * 1000,
        }
        installedProof = { proof, generation: attemptGeneration }
        if (attemptGeneration !== generation) throw new Error('PASSWORD_RECOVERY_SESSION_MISSING')
        verifiedProof = proof
        cleanupProof = null
        try {
          options.storage?.set(proof)
        } catch {
          /* Recovery still works without browser persistence. */
        }
        return result.data.session
      }
      attempt = queue.then(run)
      attempts.set(key, attempt)
      queue = attempt.then(
        () => undefined,
        () => undefined,
      )
      void attempt.then(
        () => {
          // Retain only the successful grant and its proof in memory.
          if (completedKey && completedKey !== key) attempts.delete(completedKey)
          completedKey = key
        },
        () => {
          if (attempts.get(key) === attempt) attempts.delete(key)
        },
      )
    }
    const expectedGeneration = generation
    return attempt.then((session) => {
      const proof = installedProof?.proof
      if (!proof || proof.userId !== session.user.id)
        throw new Error('PASSWORD_RECOVERY_SESSION_CHANGED')
      return requireVerifiedSession(proof, expectedGeneration)
    })
  }

  return {
    establish,
    clear(): Promise<boolean> {
      const releasedGeneration = generation
      const proofBeforeClear = verifiedProof ?? cleanupProof
      const pending = queue
      generation += 1
      attempts.clear()
      verifiedProof = null
      completedKey = null
      try {
        options.storage?.clear()
      } catch {
        /* Runtime proof is cleared even if storage is unavailable. */
      }
      // A pending verify can still install its session inside the SDK. Callers
      // wait for it before signing out so it cannot restore auth after exit.
      return pending.then(async () => {
        const ownProof =
          installedProof?.generation === releasedGeneration
            ? installedProof.proof
            : proofBeforeClear
        // Preserve ownership across a failed session read or sign-out. It is
        // cleanup-only proof: it cannot authorize another password update.
        cleanupProof = ownProof
        const current = await auth.getSession()
        if (current.error) throw current.error
        const ownsSession = matchesProof(current.data.session, ownProof)
        if (!ownsSession) cleanupProof = null
        if (installedProof?.generation === releasedGeneration) installedProof = null
        return ownsSession
      })
    },
  }
}
